import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

// TODO abortcontroller
import { CreditCardApplicationContext } from '../../../CreditCardApplicationContext';
import {
  CenteredCard,
  ErrorCard,
  ExportErrorCard,
  LoadingCard,
} from 'src/commons/components/Card';
import Heading from 'src/commons/components/Heading';
import { HiddenAlert } from 'src/commons/components/HiddenAlert';
import { Spinner } from 'src/commons/components/Spinner';
import {
  CARD_BREAKPOINT,
  PageNames,
  ApplicationStatus,
  CARD_SERVICING_PATHS,
  CARDSTAR_OVERRIDE_ACCOUNT_CREATION,
} from 'src/commons/constants';
import { ifFeatureFlagOn } from 'src/commons/utilities/feature-flags';
import { prefixRoute } from 'src/commons/utilities/routes';
import {
  useApplicationPolling,
  PollingStatus,
} from 'src/commons/utilities/useApplicationPolling';

// ExportWaitingRoom
// Poll the application while the status is EXPORT_IN_PROGRESS
// Show a Spinner while polling app once per second with a timeout of 60 seconds
// Route to the FinishUp page when APPLICATION_COMPLETED comes through successfully
// Display ExportErrorCard when EXPORT_ERROR occurs
// Display the ErrorModal when timeout occurs

// Possible statuses at conclusion of app polling:
//  - APPLICATION_COMPLETED : Route to FinishUp page
//  - EXPORT_ERROR          : Show ExportErrorCard
//  - EXPORT_IN_PROGRESS    : Show ErrorCard

// Constants --------------------------------------------------------------------------------------
const POLLING_TIMEOUT = 60000; // maximum time in ms to poll the application

// #region -- Styles ------------------------------------------------------------------------------
const WaitingCard = styled(CenteredCard)`
  padding: 3.625rem 5rem;
  @media (max-width: ${CARD_BREAKPOINT}) {
    padding: 3.3125rem 2rem;
  }
`;
WaitingCard.displayName = 'WaitingCard';

// Centers the Spinner and StyledHeading
const Content = styled.div`
  margin: 3.875rem 0 3.3125rem;
  @media (max-width: ${CARD_BREAKPOINT}) {
    margin: 0 0 6.75rem;
  }
`;
Content.displayName = 'Content';

const StyledHeading = styled(Heading)`
  margin: 1.375rem 0 0;
  max-width: 19rem;
  line-height: 2rem;
  @media (max-width: ${CARD_BREAKPOINT}) {
    margin: 2.125rem 0 0;
  }
`;
StyledHeading.displayName = 'StyledHeading';
// #endregion

export const ExportWaitingRoom = () => {
  const { fetchAndResumeCreditCardApplication, fetchingApplication } =
    useContext(CreditCardApplicationContext);

  // State ----------------------------------------------------------------------------------------
  const [polling, setPolling] = useState(true); // true while polling application
  const [success, setSuccess] = useState(false); // true when APPLICATION_COMPLETED
  const [exportError, setExportError] = useState(false); // show export error card if EXPORT_ERROR
  const [error, setError] = useState(false); // show error if networking failed or on timeout
  const [timedOut, setTimedOut] = useState(false); // true if max time limit is exceeded

  // Hooks ----------------------------------------------------------------------------------------
  const { applicationId } = useParams();
  const [appPollState] = useApplicationPolling(applicationId, polling);
  const isCardStarRedirectOn = ifFeatureFlagOn(
    CARDSTAR_OVERRIDE_ACCOUNT_CREATION
  );

  // Route to FinishUp page upon successful Fiserv export
  const navigate = useNavigate();

  // Redirect if necessary based on current application status
  useEffect(() => {
    fetchAndResumeCreditCardApplication(PageNames.EXPORT_WAITING_ROOM);
  }, []);

  // Route to FinishUp page if polling concludes with APPLICATION_COMPLETED
  useEffect(() => {
    if (success && !polling) {
      if (isCardStarRedirectOn) {
        window.location.href = CARD_SERVICING_PATHS.dashboard;
        return;
      }
      navigate(prefixRoute(`/application/${applicationId}/finish-up`));
    } else if (!polling) {
      fetchAndResumeCreditCardApplication(PageNames.EXPORT_WAITING_ROOM);
    }
  }, [polling]);

  // Poll application - timeout after max time elapses and show ErrorModal
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (!timedOut) {
      // Start/restart timeout
      timer = setTimeout(() => {
        setTimedOut(true);
        setPolling(false);
      }, POLLING_TIMEOUT);
    } else if (timedOut && !polling && !exportError && !success) {
      // Timeout occurred without status transition from EXPORT_IN_PROGRESS - show ErrorModal
      setError(true);
    }
    return () => clearTimeout(timer);
  }, [timedOut]);

  // Continue polling application until status transitions from EXPORT_IN_PROGRESS
  useEffect(() => {
    // Check application status while polling is underway
    if (appPollState.pollingStatus === PollingStatus.ACTIVE) {
      const { application } = appPollState;
      const { applicationStatus } = application;

      switch (applicationStatus) {
        case ApplicationStatus.APPLICATION_COMPLETED:
          // Happy Path - Stop polling when Fiserv export concludes successfully
          setSuccess(true);
          setPolling(false);
          break;
        case ApplicationStatus.EXPORT_ERROR:
          // Export Failure - Stop polling and show ExportErrorCard
          setExportError(true);
          setPolling(false);
          break;
        case ApplicationStatus.EXPORT_IN_PROGRESS:
          // In Progress - Continue polling until application status changes
          break;
        default:
          // Unexpected Status - Stop polling and redirect accordingly
          setPolling(false);
          break;
      }
    } else if (appPollState.pollingStatus === PollingStatus.FAILURE) {
      // Stop polling if an error occurred fetching the application
      setError(true);
      setPolling(false);
    }
  }, [appPollState]);

  if (fetchingApplication) {
    return (
      <>
        <HiddenAlert
          id="export-waiting-room-loading-hidden-alert"
          alertText="loading"
        />
        <LoadingCard />
      </>
    );
  }

  if (exportError) {
    return <ExportErrorCard />;
  }

  if (error) {
    return <ErrorCard />;
  }

  return (
    <WaitingCard
      id="export-waiting-room-card"
      role="alert"
      aria-labelledby="export-waiting-room-heading"
    >
      <Content id="export-waiting-room-card-content">
        <Spinner />
        <StyledHeading id="export-waiting-room-heading">
          Saving your info...
        </StyledHeading>
      </Content>
    </WaitingCard>
  );
};
